import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Heart, Eye, MessageCircle, Send, PlayCircle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "./HeroSlides";

const deviceId = () => {
  let d = localStorage.getItem("lif_device");
  if (!d) { d = `d${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("lif_device", d); }
  return d;
};
const thumb = (v) => (v.kind === "youtube" ? `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg` : null);

const pl = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

function Player({ v, onView, picked }) {
  useEffect(() => { if (picked && v.kind === "youtube") onView(v.video_id); }, [v.video_id, picked]); // eslint-disable-line
  return (
    <div className="aspect-video bg-black rounded-2xl overflow-hidden" data-testid="video-player">
      {v.kind === "youtube"
        ? <iframe title={v.title} src={`https://www.youtube-nocookie.com/embed/${v.youtube_id}?rel=0`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen />
        : <video key={v.video_id} src={fileUrl(v.path)} controls playsInline onPlay={() => onView(v.video_id)} className="w-full h-full" />}
    </div>
  );
}

function Comments({ v, onCount }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ name: localStorage.getItem("lif_name") || "", text: "" });
  useEffect(() => { api.get(`/public/videos/${v.video_id}/comments`).then((r) => setRows(r.data)).catch(() => {}); }, [v.video_id]);
  async function post(e) {
    e.preventDefault();
    try {
      const { data } = await api.post(`/public/videos/${v.video_id}/comments`, f);
      localStorage.setItem("lif_name", f.name); setRows([data, ...rows]); setF({ ...f, text: "" }); onCount(1);
    } catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className="mt-6" data-testid="video-comments">
      <p className="text-sm font-medium mb-3">{rows.length} comment{rows.length === 1 ? "" : "s"}</p>
      <form onSubmit={post} className="flex flex-col sm:flex-row gap-2 mb-4">
        <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={60} placeholder="Your name" className="input-luxe sm:!w-40" data-testid="video-comment-name" />
        <input required value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} maxLength={500} placeholder="Add a comment…" className="input-luxe flex-1" data-testid="video-comment-text" />
        <button className="btn-gold text-xs" data-testid="video-comment-send"><Send className="w-3 h-3" /> Post</button>
      </form>
      <div className="space-y-3">
        {rows.map((c) => (
          <div key={c.comment_id} className="flex gap-3" data-testid={`video-comment-${c.comment_id}`}>
            <span className="w-8 h-8 shrink-0 rounded-full bg-[var(--bg-warm)] flex items-center justify-center text-xs font-medium">{c.name[0]?.toUpperCase()}</span>
            <div><p className="text-xs"><b>{c.name}</b> <span className="text-[var(--muted)]">{new Date(c.at).toLocaleDateString()}</span></p><p className="text-sm">{c.text}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Playlist({ vids, cur, pick }) {
  return (
    <div className="space-y-2 lg:max-h-[560px] lg:overflow-y-auto pr-1" data-testid="video-playlist">
      {vids.map((v, i) => (
        <button key={v.video_id} onClick={() => pick(i)} data-testid={`video-item-${i}`} className={`w-full flex gap-3 text-left rounded-xl p-2 transition-colors ${i === cur ? "bg-[var(--bg-warm)]" : "hover:bg-black/[0.03]"}`}>
          <div className="relative w-36 shrink-0 aspect-video rounded-lg overflow-hidden bg-black">
            {thumb(v) ? <img src={thumb(v)} alt="" className="w-full h-full object-cover" /> : <video src={`${fileUrl(v.path)}#t=1`} muted preload="metadata" className="w-full h-full object-cover" />}
            <PlayCircle className="absolute inset-0 m-auto w-7 h-7 text-white/90" />
          </div>
          <div className="min-w-0"><p className="text-sm font-medium line-clamp-2">{v.title}</p><p className="text-[11px] text-[var(--muted)] mt-1">{pl(v.views, "view")} · {pl(v.likes, "like")}</p></div>
        </button>
      ))}
    </div>
  );
}

export function VideoBox({ slug }) {
  const [vids, setVids] = useState([]);
  const [cur, setCur] = useState(0);
  const [picked, setPicked] = useState(false);
  const dev = useMemo(deviceId, []);
  useEffect(() => { api.get(`/public/weddings/${slug}/videos?device_id=${dev}`).then((r) => setVids(r.data)).catch(() => {}); }, [slug, dev]);
  if (!vids.length) return null;
  const v = vids[cur];
  const patch = (p) => setVids(vids.map((x, i) => (i === cur ? { ...x, ...p } : x)));
  const view = (id) => {
    const k = `lif_viewed_${id}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, "1");
    api.post(`/public/videos/${id}/view`).then(() => setVids((l) => l.map((x) => (x.video_id === id ? { ...x, views: x.views + 1 } : x)))).catch(() => {});
  };
  async function like() {
    try { const { data } = await api.post(`/public/videos/${v.video_id}/like`, { device_id: dev }); patch({ liked: data.liked, likes: data.likes }); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <section className="section-pad" data-testid="site-video-box">
      <div className="container-wide px-6">
        <p className="font-script text-[var(--gold)] text-4xl mb-2 text-center">Our Videos</p>
        <h2 className="font-serif font-light text-4xl md:text-5xl mb-10 text-center">Watch our memories</h2>
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div>
            <Player v={v} onView={view} picked={picked} />
            <h3 className="font-serif text-2xl mt-4" data-testid="video-title">{v.title}</h3>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[var(--muted)]">
              <span className="inline-flex items-center gap-1" data-testid="video-views"><Eye className="w-4 h-4" /> {pl(v.views, "view")}</span>
              <button onClick={like} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border transition-colors ${v.liked ? "border-rose-300 bg-rose-50 text-rose-600" : "border-[var(--line)] hover:border-[var(--gold)]"}`} data-testid="video-like"><Heart className={`w-4 h-4 ${v.liked ? "fill-current" : ""}`} /> {v.likes}</button>
              <span className="inline-flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {v.comments}</span>
            </div>
            <Comments v={v} onCount={(n) => patch({ comments: v.comments + n })} />
          </div>
          <Playlist vids={vids} cur={cur} pick={(i) => { setCur(i); setPicked(true); }} />
        </div>
      </div>
    </section>
  );
}
